import { useCallback, useEffect, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { RouteChildrenProps } from 'react-router';
import { Queue } from '@rossum/api-client/queues';
import { User } from '@rossum/api-client/users';
import { Header } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { Tile } from '@rossum/components/tile';
import { IconBan, IconCheck, IconUsers } from '@rossum/ui/icons/tabler';
import { Box, Button, Stack, SvgIcon, Typography } from '@rossum/ui/material';
import { useUsers } from '../../../../business/users/useUsers';
import InfoPlaceholder from '../../../../components/UI/InfoPlaceHolder';
import { pageSizeOptions } from '../../../../constants/values';
import { QueueSettingsBreadcrumbs } from '../../../../features/queue-settings/components/QueueSettingsBreadcrumbs';
import { QueueSettingsTabs } from '../../../../features/queue-settings/components/QueueSettingsTabs';
import { usePatchQueue } from '../../../../features/queues/hooks/usePatchQueue';
import { asScalar, parse, stringify } from '../../../../lib/url';
import {
  getRoleName,
  isUserAdminLike,
} from '../../../../redux/modules/groups/helpers';
import { groupsSelector } from '../../../../redux/modules/groups/selectors';
import { getName } from '../../../../redux/modules/user/helpers';
import { userSelector } from '../../../../redux/modules/user/selectors';
import ListFooter from './ListFooter';
import { ToggleAllDialog, ToggleAllDialogAction } from './ToggleAllDialog';
import { useManageableUsersCount } from './useManageableUsersCount';

type QueueAccessProps = RouteChildrenProps & {
  selectedQueue: Queue;
  workspaceName: string;
};

const defaultQuery = { pageSize: 15, page: 1, ordering: 'username' };

const QueueAccess = ({
  selectedQueue,
  history,
  workspaceName,
  location,
  match,
}: QueueAccessProps) => {
  const [dialogAction, setDialogAction] = useState<ToggleAllDialogAction>();
  const { search = '', ...query } = parse(location.search);
  const searchValue = R.isString(search) ? search : '';

  const parentPath = match?.url ?? '';

  const intl = useIntl();

  const groups = useSelector(groupsSelector);

  const currentUserUrl = useSelector(userSelector).url;

  const { mutateAsync: patchQueue } = usePatchQueue();

  const { data: usersData, isFetching: usersDataFetching } = useUsers({
    deleted: false,
    pageSize: Number(query['pageSize']) || defaultQuery.pageSize,
    page: Number(query['page']) || defaultQuery.page,
    search: searchValue || undefined,
    ordering: asScalar(query['ordering']) || defaultQuery.ordering,
  });

  const users = usersData?.results ?? [];
  const totalUsers = usersData?.pagination.total ?? 0;
  const totalPages = usersData?.pagination.totalPages;

  // Admins have access via their role and the current user can't manage their
  // own access — both have disabled toggles, so neither is counted or acted on
  // by the batch Enable/Disable All buttons.
  const isAdminLikeUser = useCallback(
    (user: User) => isUserAdminLike(getRoleName(user.groups, groups)),
    [groups]
  );

  const { count, batchButtonsDisabled } = useManageableUsersCount({
    searchValue,
    totalUsers,
    usersDataFetching,
  });

  // reset query params if they are not valid
  useEffect(() => {
    const parsedQuery = parse(location.search) || {};
    if (
      !parsedQuery['ordering'] ||
      !parsedQuery['page'] ||
      !parsedQuery['pageSize'] ||
      !pageSizeOptions.some(
        option => option.toString() === parsedQuery['pageSize']
      )
    ) {
      history.replace({
        state: location.state,
        search: stringify(defaultQuery),
      });
    }
  }, []); // oxlint-disable-line react-hooks/exhaustive-deps

  const setSearchPhrase = (value: string) =>
    history.replace({
      state: location.state,
      search: stringify(
        R.pickBy({ ...query, search: value, page: 1 }, R.isTruthy)
      ),
    });

  const isEnabled = (user: User) => selectedQueue.users.includes(user.url);

  const onToggle = (enabled: boolean, user: User) => {
    const resolvedPayload = {
      users: enabled
        ? [...selectedQueue.users, user.url]
        : selectedQueue.users.filter(userUrl => userUrl !== user.url),
    };
    patchQueue({
      queueId: selectedQueue.id,
      payload: resolvedPayload,
    });
  };

  const onToggleAllSubmit = useCallback(
    (filteredUsers: User[]) => {
      // Neither admins (access is via role) nor the current user (can't manage
      // their own access) can be batch toggled, so the action only touches the
      // rest. Because the current user is never in `manageableUrls`, they are
      // always kept — no special-casing needed.
      const manageableUrls = filteredUsers
        .filter(user => !isAdminLikeUser(user) && user.url !== currentUserUrl)
        .map(u => u.url);

      if (!dialogAction || !manageableUrls.length) {
        setDialogAction(undefined);
        return;
      }

      // Membership is replaced wholesale on PATCH, so start from the current
      // members — minus any admins, which must never be persisted into the
      // list — then add or remove the manageable users.
      const adminUrls = filteredUsers.filter(isAdminLikeUser).map(u => u.url);
      const existingUsers = selectedQueue.users.filter(
        url => !adminUrls.includes(url)
      );

      const updatedUsers =
        dialogAction === 'enableUsersOnQueue'
          ? R.unique([...existingUsers, ...manageableUrls])
          : existingUsers.filter(url => !manageableUrls.includes(url));

      patchQueue({
        queueId: selectedQueue.id,
        payload: { users: updatedUsers },
      });
      setDialogAction(undefined);
    },
    [
      currentUserUrl,
      dialogAction,
      isAdminLikeUser,
      patchQueue,
      selectedQueue.id,
      selectedQueue.users,
    ]
  );

  return (
    <PageLayout
      renderHeader={params => (
        <Header
          scrollableDivRef={params.scrollableDivRef}
          breadcrumbs={
            <QueueSettingsBreadcrumbs
              queue={selectedQueue}
              workspaceName={workspaceName}
              breadcrumbs={[
                {
                  label: intl.formatMessage({
                    id: 'containers.settings.queues.queue.access',
                  }),
                },
              ]}
            />
          }
          tabs={<QueueSettingsTabs parentPath={parentPath} value="access" />}
          title={intl.formatMessage({
            id: 'containers.settings.queues.queue.access',
          })}
          description={intl.formatMessage({
            id: 'containers.settings.queues.queue.access.description',
          })}
        />
      )}
    >
      <Box sx={{ p: 4 }} data-page-title="queue-access">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack sx={{ flex: 1 }}>
            <Typography variant="h6">
              {intl.formatMessage({
                id: 'containers.settings.queues.queue.title',
              })}
            </Typography>
          </Stack>
          {!batchButtonsDisabled && (
            <Stack direction="row" spacing={1}>
              <Button
                color="secondary"
                size="small"
                startIcon={
                  <SvgIcon fontSize="large">
                    <IconCheck />
                  </SvgIcon>
                }
                data-cy="queue-access-enable-all"
                onClick={() => setDialogAction('enableUsersOnQueue')}
              >
                {intl.formatMessage(
                  { id: 'containers.users.components.workspace.enableAll' },
                  { count }
                )}
              </Button>
              <Button
                color="secondary"
                size="small"
                data-cy="queue-access-disable-all"
                onClick={() => setDialogAction('disableUsersOnQueue')}
                startIcon={
                  <SvgIcon fontSize="large">
                    <IconBan />
                  </SvgIcon>
                }
              >
                {intl.formatMessage(
                  { id: 'containers.users.components.workspace.disableAll' },
                  { count }
                )}
              </Button>
            </Stack>
          )}
          <SearchInput
            value={searchValue}
            onChange={setSearchPhrase}
            placeholder={intl.formatMessage({
              id: 'containers.settings.queues.queue.searchPlaceholder',
            })}
          />
        </Stack>
        {users.length ? (
          <Stack direction="column" spacing={0.5} pt={2}>
            {users.map(user => {
              const userRole = getRoleName(user.groups, groups);
              const isAdmin = isAdminLikeUser(user);
              const isCurrentUser = user.url === currentUserUrl;

              return (
                <Tile
                  key={user.id}
                  title={getName({
                    firstName: user.firstName,
                    lastName: user.lastName,
                    username: user.username,
                    email: user.email,
                  })}
                  description={user.email}
                  endChips={
                    userRole
                      ? [
                          {
                            key: 'role',
                            label:
                              userRole === 'organization_group_admin'
                                ? intl.formatMessage({
                                    id: 'containers.settings.queues.queue.orgGroupAdmin',
                                  })
                                : intl.formatMessage({
                                    id: `containers.settings.users.roles.${userRole}`,
                                  }),
                          },
                        ]
                      : undefined
                  }
                  toggleAction={{
                    checked: isEnabled(user) || isAdmin || isCurrentUser,
                    disabled: !selectedQueue || isAdmin || isCurrentUser,
                    onChange: () => onToggle(!isEnabled(user), user),
                    tooltip: isAdmin
                      ? intl.formatMessage({
                          id: 'containers.settings.queues.queue.admin.tooltip',
                        })
                      : isCurrentUser
                        ? intl.formatMessage({
                            id: 'containers.settings.queues.queue.currentUser.tooltip',
                          })
                        : undefined,
                    'data-cy': 'queue-access-enable-user-toggle',
                  }}
                  dragHandle={null}
                />
              );
            })}
          </Stack>
        ) : (
          <InfoPlaceholder
            icon={IconUsers}
            title="containers.settings.queues.noUserFound"
            text="containers.settings.queues.noUserFound.text"
          />
        )}
      </Box>
      <ToggleAllDialog
        onSubmit={onToggleAllSubmit}
        searchValue={searchValue}
        onClose={() => setDialogAction(undefined)}
        action={dialogAction}
        queueName={selectedQueue.name}
        count={count}
      />
      <ListFooter listLength={totalUsers} totalPages={totalPages} />
    </PageLayout>
  );
};

export default QueueAccess;
